/* @img-migrated */
import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import {
  Heart, Send, ImageIcon, Trash2, X, MessageSquare, ThumbsUp,
  Calendar, MapPin, Clock, ArrowRight, MoreHorizontal, Pencil, AlertTriangle,
  Share2, FileText, ExternalLink
} from 'lucide-react';
import { LeadershipBadge } from '../../components/LeadershipBadge';
import { MultiImageUploader } from '../../components/MultiImageUploader';
import { PostImageCarousel } from '../../components/PostImageCarousel';
import { SharePostMenu } from '../../components/community/SharePostMenu';

import Img from '../../components/Img';
import { resolveImg } from '../../lib/images';
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getTimeAgo = (dateStr) => {
  const now = new Date();
  const date = new Date(dateStr);
  const diff = Math.floor((now - date) / 1000);
  if (diff < 60) return 'Just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const avatarColors = [
  'from-[#E63946] to-[#C62D3A]', 'from-[#457B9D] to-[#1D3557]',
  'from-[#F4A261] to-[#E76F51]', 'from-[#2A9D8F] to-[#264653]',
  'from-[#6C5CE7] to-[#A29BFE]', 'from-[#00B894] to-[#00CEC9]',
];
const getAvatarColor = (userId) => {
  const hash = (userId || '').split('').reduce((a, c) => a + c.charCodeAt(0), 0);
  return avatarColors[hash % avatarColors.length];
};

const Avatar = ({ user, size = 'w-10 h-10', textSize = 'text-sm' }) => {
  const pic = user?.picture;
  if (pic) return <Img variant="generic" src={resolveImg(pic)} alt="" className={`${size} rounded-full object-cover`} />;
  return (
    <div className={`${size} rounded-full bg-gradient-to-br ${getAvatarColor(user?.id)} flex items-center justify-center text-white font-semibold ${textSize} shadow-sm`}>
      {user?.name?.charAt(0).toUpperCase() || '?'}
    </div>
  );
};

const PortalCommunity = () => {
  const { user } = useAuth();
  const [posts, setPosts] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newPost, setNewPost] = useState('');
  const [postImages, setPostImages] = useState([]);
  const [posting, setPosting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [expandedComments, setExpandedComments] = useState({});
  const [comments, setComments] = useState({});
  const [newComment, setNewComment] = useState({});
  const [commentSubmitting, setCommentSubmitting] = useState({});
  const [composerFocused, setComposerFocused] = useState(false);
  const [openMenu, setOpenMenu] = useState(null);
  const [editingPost, setEditingPost] = useState(null);
  const [editContent, setEditContent] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [shareOpen, setShareOpen] = useState(null);

  const isAdmin = ['super_admin', 'membership_lead', 'membership_support', 'marketing_team'].includes(user?.role);

  useEffect(() => { fetchPosts(); fetchEvents(); }, []);  

  const fetchPosts = async () => {
    try {
      const res = await axios.get(`${API}/portal/community/feed?limit=30`, { withCredentials: true });
      setPosts(res.data.items || []);
    } catch { /* posts load silently */ } finally { setLoading(false); }
  };

  const fetchEvents = async () => {
    try {
      const res = await axios.get(`${API}/events?limit=3`, { withCredentials: true });
      setEvents(res.data.filter(e => e.date >= new Date().toISOString().split('T')[0]).slice(0, 3));
    } catch { /* events load silently */ }
  };

  const handlePost = async () => {
    if (!newPost.trim() && postImages.length === 0) return;
    setPosting(true);
    try {
      await axios.post(`${API}/portal/community`, { content: newPost, images: postImages, post_type: postImages.length > 0 ? 'photo' : 'update' }, { withCredentials: true });
      setNewPost(''); setPostImages([]); setComposerFocused(false); fetchPosts();
    } catch (err) { toast.error('Failed to post'); } finally { setPosting(false); }
  };

  const handleLike = async (postId) => {
    setPosts(prev => prev.map(p => p.id === postId ? { ...p, likes_count: p.user_liked ? Math.max((p.likes_count || 0) - 1, 0) : (p.likes_count || 0) + 1, user_liked: !p.user_liked } : p));
    try { await axios.post(`${API}/portal/community/${postId}/like`, {}, { withCredentials: true }); } catch { fetchPosts(); }
  };

  const toggleComments = async (postId) => {
    if (expandedComments[postId]) { setExpandedComments(prev => ({ ...prev, [postId]: false })); return; }
    setExpandedComments(prev => ({ ...prev, [postId]: true }));
    if (!comments[postId]) {
      try { const res = await axios.get(`${API}/portal/community/${postId}/comments`, { withCredentials: true }); setComments(prev => ({ ...prev, [postId]: res.data })); } catch { toast.error('Failed to load comments'); }
    }
  };

  const handleComment = async (postId) => {
    const text = newComment[postId]?.trim();
    if (!text) return;
    setCommentSubmitting(prev => ({ ...prev, [postId]: true }));
    try {
      const fd = new FormData(); fd.append('content', text);
      await axios.post(`${API}/portal/community/${postId}/comments`, fd, { withCredentials: true });
      setNewComment(prev => ({ ...prev, [postId]: '' }));
      const res = await axios.get(`${API}/portal/community/${postId}/comments`, { withCredentials: true });
      setComments(prev => ({ ...prev, [postId]: res.data }));
      setPosts(prev => prev.map(p => p.id === postId ? { ...p, comments_count: (p.comments_count || 0) + 1 } : p));
    } catch { toast.error('Failed'); } finally { setCommentSubmitting(prev => ({ ...prev, [postId]: false })); }
  };

  const handleEdit = async (postId) => {
    if (!editContent.trim()) return;
    try {
      await axios.put(`${API}/portal/community/${postId}`, { content: editContent }, { withCredentials: true });
      setPosts(prev => prev.map(p => p.id === postId ? { ...p, content: editContent } : p));
      setEditingPost(null); toast.success('Post updated');
    } catch { toast.error('Failed to update'); }
  };

  const handleDelete = async (postId) => {
    setPosts(prev => prev.filter(p => p.id !== postId));
    setDeleteConfirm(null);
    try { await axios.delete(`${API}/portal/community/${postId}`, { withCredentials: true }); toast.success('Post deleted'); } catch { fetchPosts(); }
  };

  const handleDeleteComment = async (postId, commentId) => {
    if (!window.confirm('Delete this comment?')) return;
    try {
      await axios.delete(`${API}/portal/community/${postId}/comments/${commentId}`, { withCredentials: true });
      setComments(prev => ({ ...prev, [postId]: (prev[postId] || []).filter(c => c.id !== commentId) }));
      setPosts(prev => prev.map(p => p.id === postId ? { ...p, comments_count: Math.max((p.comments_count || 1) - 1, 0) } : p));
      toast.success('Comment deleted');
    } catch { toast.error('Failed to delete comment'); }
  };

  // Build the share URL on demand
  const shareUrl = (postId) => `${window.location.origin}/post/${postId}`;

  if (loading) return <div className="flex items-center justify-center min-h-[60vh]"><div className="animate-spin w-8 h-8 border-4 border-[#E63946] border-t-transparent rounded-full" /></div>;

  return (
    <div className="min-h-screen bg-[#F0F2F5]" data-testid="portal-community">
      {/* Delete Confirmation Modal */}
      <AnimatePresence>
        {deleteConfirm && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setDeleteConfirm(null)}>
            <motion.div initial={{ scale: 0.95 }} animate={{ scale: 1 }} exit={{ scale: 0.95 }} onClick={e => e.stopPropagation()} className="bg-white rounded-xl p-6 max-w-sm w-full shadow-xl" data-testid="delete-confirm-modal">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center"><AlertTriangle className="w-5 h-5 text-red-600" /></div>
                <h3 className="text-lg font-bold text-[#050505]">Delete Post?</h3>
              </div>
              <p className="text-sm text-[#65676B] mb-5">This action cannot be undone. The post and all its comments will be permanently removed.</p>
              <div className="flex gap-3">
                <button onClick={() => setDeleteConfirm(null)} className="flex-1 py-2.5 rounded-lg border border-slate-200 text-sm font-semibold text-[#050505] hover:bg-slate-50 transition-colors" data-testid="delete-cancel-btn">Cancel</button>
                <button onClick={() => handleDelete(deleteConfirm)} className="flex-1 py-2.5 rounded-lg bg-red-600 text-white text-sm font-semibold hover:bg-red-700 transition-colors" data-testid="delete-confirm-btn">Delete</button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="max-w-[1080px] mx-auto px-4 py-5">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_340px] gap-5">
          <div className="space-y-4">
            {/* POST COMPOSER */}
            <div className="bg-white rounded-xl shadow-[0_1px_2px_rgba(0,0,0,0.1)] overflow-hidden" data-testid="post-composer">
              <div className="p-4">
                <div className="flex items-start gap-3">
                  <Link to={`/portal/members/${user?.id}`}><Avatar user={user} /></Link>
                  <div className="flex-1">
                    <textarea value={newPost} onChange={e => setNewPost(e.target.value)} onFocus={() => setComposerFocused(true)}
                      placeholder="What would you like to share with the DCCF community?"
                      rows={composerFocused || newPost ? 3 : 1}
                      className="w-full bg-[#F0F2F5] rounded-2xl px-4 py-2.5 text-[15px] placeholder:text-[#65676B] text-[#1C1E21] resize-none border-0 focus:outline-none focus:bg-[#E8EAED] transition-colors"
                      data-testid="post-content-input" />
                  </div>
                </div>
                {postImages.length > 0 && (
                  <div className="mt-3 ml-[52px]">
                    <MultiImageUploader
                      value={postImages}
                      onChange={setPostImages}
                      max={5}
                      label="Add more photos"
                      dataTestId="composer-uploader"
                    />
                  </div>
                )}
              </div>
              <div className="flex items-center justify-between px-4 py-2.5 border-t border-[#E4E6EB]">
                {postImages.length === 0 ? (
                  <label className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-[#65676B] hover:bg-[#F0F2F5] cursor-pointer transition-colors" data-testid="post-image-upload">
                    <ImageIcon className="w-5 h-5 text-[#45BD62]" />
                    <span className="text-[13px] font-semibold hidden sm:inline">{uploading ? 'Uploading...' : 'Photos'}</span>
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={async (e) => {
                        const files = Array.from(e.target.files || []).slice(0, 5);
                        e.target.value = '';
                        if (files.length === 0) return;
                        setUploading(true);
                        try {
                          const fd = new FormData();
                          files.forEach(f => fd.append('files', f));
                          const res = await axios.post(`${API}/portal/community/upload-multi`, fd, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
                          setPostImages(prev => [...prev, ...(res.data?.image_urls || [])]);
                        } catch (err) {
                          toast.error(err?.response?.data?.detail || 'Failed to upload');
                        } finally { setUploading(false); }
                      }}
                      className="hidden"
                    />
                  </label>
                ) : (
                  <span className="text-[12px] text-[#65676B] font-medium" data-testid="composer-image-count">
                    {postImages.length} / 5 photo{postImages.length !== 1 ? 's' : ''}
                  </span>
                )}
                <button onClick={handlePost} disabled={posting || (!newPost.trim() && postImages.length === 0)}
                  className="px-5 py-1.5 bg-[#E63946] hover:bg-[#D32F3F] disabled:bg-[#E4E6EB] disabled:text-[#BCC0C4] text-white text-[13px] font-semibold rounded-lg transition-colors" data-testid="publish-post-btn">
                  {posting ? 'Posting...' : 'Post'}
                </button>
              </div>
            </div>

            {/* FEED */}
            {posts.length > 0 ? posts.map((post, index) => {
              // Blog card
              if (post.kind === 'blog') {
                return (
                  <Link key={`blog-${post.id}`} to={`/news/${post.id}`} className="block" data-testid={`community-blog-${index}`}>
                    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: Math.min(index * 0.04, 0.2) }}
                      className="bg-white rounded-xl shadow-[0_1px_2px_rgba(0,0,0,0.1)] overflow-hidden hover:shadow-md transition-shadow">
                      {post.image_url && (
                        <div className="relative">
                          <Img variant="generic" src={resolveImg(post.image_url)} alt={post.title} className="w-full h-56 sm:h-64 object-cover" />
                          <span className="absolute top-3 left-3 inline-flex items-center gap-1 bg-amber-100 text-amber-800 border border-amber-200 text-[10.5px] font-bold uppercase tracking-wider px-2 py-1 rounded-md shadow-sm">
                            <FileText className="w-3 h-3" /> Blog
                          </span>
                        </div>
                      )}
                      <div className="p-5">
                        {!post.image_url && (
                          <span className="inline-flex items-center gap-1 bg-amber-100 text-amber-800 border border-amber-200 text-[10.5px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-md mb-2">
                            <FileText className="w-3 h-3" /> Blog
                          </span>
                        )}
                        <h3 className="text-[17px] font-bold text-[#050505] leading-tight">{post.title}</h3>
                        {post.excerpt && <p className="text-[13.5px] text-[#65676B] mt-1.5 line-clamp-2 leading-relaxed">{post.excerpt}</p>}
                        <p className="text-[12px] text-[#002249] font-semibold mt-3 inline-flex items-center gap-1">Read full article <ArrowRight className="w-3.5 h-3.5" /></p>
                      </div>
                    </motion.div>
                  </Link>
                );
              }
              // Ad card
              if (post.kind === 'ad') {
                const media = post.media?.[0] || {};
                const isVideo = media.type === 'video';
                const adContent = (
                  <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
                    className="bg-white rounded-xl shadow-[0_1px_2px_rgba(0,0,0,0.1)] overflow-hidden border border-slate-200/70" data-testid={`community-ad-${index}`}>
                    <div className="px-4 pt-3 pb-2 flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="inline-flex items-center gap-1 bg-slate-100 text-slate-600 text-[10.5px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-md">
                          Sponsored
                        </span>
                        {post.company_name && <span className="text-[12.5px] text-[#65676B]">· {post.company_name}</span>}
                      </div>
                    </div>
                    {isVideo ? (
                      <video src={resolveImg(media.url)} muted autoPlay loop playsInline className="w-full max-h-[480px] object-cover" />
                    ) : (
                      <Img variant="generic" src={resolveImg(media.url)} alt={post.name} className="w-full max-h-[480px] object-cover" />
                    )}
                    {post.cta_text && (
                      <div className="px-4 py-3 flex items-center justify-between border-t border-slate-100">
                        <p className="text-[13.5px] font-semibold text-[#050505]">{post.name}</p>
                        <span className="inline-flex items-center gap-1 text-[#002249] text-[13px] font-bold hover:underline">
                          {post.cta_text} <ExternalLink className="w-3.5 h-3.5" />
                        </span>
                      </div>
                    )}
                  </motion.div>
                );
                return post.cta_url ? (
                  <a key={`ad-${post.id}-${index}`} href={post.cta_url} target="_blank" rel="noopener noreferrer" className="block">{adContent}</a>
                ) : (
                  <div key={`ad-${post.id}-${index}`}>{adContent}</div>
                );
              }
              // Default: community post
              return (
              <motion.div key={post.id} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: Math.min(index * 0.04, 0.2) }}
                className="bg-white rounded-xl shadow-[0_1px_2px_rgba(0,0,0,0.1)]" data-testid={`community-post-${index}`}>
                {post.is_official && <div className="bg-[#002249] h-1 rounded-t-xl" />}
                {/* Header */}
                <div className="flex items-center justify-between px-4 pt-3 pb-1">
                  <div className="flex items-center gap-2.5">
                    {post.is_official ? (
                      <div className="w-10 h-10 rounded-full bg-[#002249] flex items-center justify-center text-white font-semibold text-sm shadow-sm">D</div>
                    ) : (
                      <Link to={`/portal/members/${post.user_id}`}>
                        <Avatar user={{ id: post.user_id, name: post.author?.name, picture: post.author?.picture }} />
                      </Link>
                    )}
                    <div>
                      <div className="flex items-center gap-1.5">
                        <p className="text-[15px] font-semibold text-[#050505] leading-tight">
                          {post.is_official ? (post.official_label || 'DCCF') : (
                            <Link to={`/portal/members/${post.user_id}`} className="hover:underline hover:text-[#002249] transition-colors" data-testid={`post-author-link-${index}`}>
                              {post.author?.name || 'Member'}
                            </Link>
                          )}
                        </p>
                        {post.is_official && <span className="text-[10px] font-bold bg-[#002249] text-white px-1.5 py-0.5 rounded">OFFICIAL</span>}
                        {!post.is_official && post.author?.leadership_level && (
                          <LeadershipBadge badges={post.author.badges} leadershipLevel={post.author.leadership_level} leadershipTitle={post.author.leadership_title} />
                        )}
                        {!post.is_official && post.author?.is_founding_member && !post.author?.leadership_level && (
                          <span className="text-[9px] font-bold bg-amber-100 text-amber-700 border border-amber-200 px-1.5 py-0.5 rounded-full flex items-center gap-0.5"><svg className="w-2.5 h-2.5" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>Founder</span>
                        )}
                      </div>
                      <div className="flex items-center gap-1 text-[12px] text-[#65676B]">
                        {!post.is_official && post.company?.name && <><span>{post.company.name}</span><span className="mx-0.5">&middot;</span></>}
                        {post.is_official && <><span className="text-[#002249] font-medium">Chamber of Commerce</span><span className="mx-0.5">&middot;</span></>}
                        <span>{getTimeAgo(post.created_at)}</span>
                        {post.updated_at && <span className="text-[#BCC0C4] ml-1">(edited)</span>}
                      </div>
                    </div>
                  </div>
                  {/* 3-DOT MENU — owner OR admin (not on official posts unless admin) */}
                  {((post.user_id === user?.id && !post.is_official) || isAdmin) && (
                    <div className="relative">
                      <button onClick={() => setOpenMenu(openMenu === post.id ? null : post.id)}
                        className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-[#F0F2F5] text-[#65676B] transition-colors" data-testid={`post-menu-${index}`}>
                        <MoreHorizontal className="w-5 h-5" />
                      </button>
                      <AnimatePresence>
                        {openMenu === post.id && (
                          <>
                            <div className="fixed inset-0 z-40" onClick={() => setOpenMenu(null)} />
                            <motion.div initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 4 }}
                              className="absolute right-0 top-full mt-1 w-44 bg-white rounded-xl shadow-lg border border-slate-200 z-50 overflow-hidden" data-testid={`post-menu-dropdown-${index}`}>
                              {post.user_id === user?.id && !post.is_official && (
                                <button onClick={() => { setEditingPost(post.id); setEditContent(post.content); setOpenMenu(null); }}
                                  className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-[#050505] hover:bg-[#F0F2F5] transition-colors" data-testid={`edit-post-${index}`}>
                                  <Pencil className="w-4 h-4 text-[#65676B]" /> Edit Post
                                </button>
                              )}
                              <button onClick={() => { setDeleteConfirm(post.id); setOpenMenu(null); }}
                                className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors" data-testid={`delete-post-btn-${index}`}>
                                <Trash2 className="w-4 h-4" /> Delete Post
                              </button>
                            </motion.div>
                          </>
                        )}
                      </AnimatePresence>
                    </div>
                  )}
                </div>

                {/* Content — editable */}
                <div className="px-4 py-2">
                  {editingPost === post.id ? (
                    <div data-testid={`edit-form-${index}`}>
                      <textarea value={editContent} onChange={e => setEditContent(e.target.value)} rows={3}
                        className="w-full bg-[#F0F2F5] rounded-xl px-4 py-2.5 text-[15px] text-[#1C1E21] resize-none border border-[#E4E6EB] focus:outline-none focus:border-[#002249]" />
                      <div className="flex gap-2 mt-2 justify-end">
                        <button onClick={() => setEditingPost(null)} className="px-4 py-1.5 text-[13px] font-semibold text-[#65676B] hover:bg-[#F0F2F5] rounded-lg">Cancel</button>
                        <button onClick={() => handleEdit(post.id)} className="px-4 py-1.5 text-[13px] font-semibold text-white bg-[#002249] hover:bg-[#001630] rounded-lg" data-testid={`save-edit-${index}`}>Save</button>
                      </div>
                    </div>
                  ) : (
                    <p className="text-[15px] text-[#050505] leading-[1.35] whitespace-pre-wrap">{post.content}</p>
                  )}
                </div>

                {/* Images — responsive carousel (1 image = full width, multi = swipe/arrows/dots) */}
                {post.images?.length > 0 && (
                  <PostImageCarousel
                    images={post.images}
                    maxHeight="max-h-[560px]"
                    dataTestId={`community-post-carousel-${index}`}
                  />
                )}

                {/* Counts */}
                {((post.likes_count || 0) > 0 || (post.comments_count || 0) > 0) && (
                  <div className="flex items-center justify-between px-4 py-2 text-[13px] text-[#65676B]">
                    {(post.likes_count || 0) > 0 ? <div className="flex items-center gap-1"><span className="w-[18px] h-[18px] bg-[#E63946] rounded-full flex items-center justify-center"><ThumbsUp className="w-[10px] h-[10px] text-white" /></span><span>{post.likes_count}</span></div> : <div />}
                    {(post.comments_count || 0) > 0 && <button onClick={() => toggleComments(post.id)} className="hover:underline">{post.comments_count} comment{post.comments_count !== 1 ? 's' : ''}</button>}
                  </div>
                )}

                {/* Actions */}
                <div className="mx-4 py-1 border-t border-[#E4E6EB] flex items-center">
                  <button onClick={() => handleLike(post.id)} className={`flex-1 flex items-center justify-center gap-1.5 py-[6px] rounded-md text-[14px] font-semibold transition-colors ${post.user_liked ? 'text-[#E63946] hover:bg-[#FEF2F2]' : 'text-[#65676B] hover:bg-[#F0F2F5]'}`} data-testid={`like-post-${index}`}>
                    <Heart className={`w-[18px] h-[18px] ${post.user_liked ? 'fill-current' : ''}`} /><span>{post.user_liked ? 'Liked' : 'Like'}</span>
                  </button>
                  <button onClick={() => toggleComments(post.id)} className="flex-1 flex items-center justify-center gap-1.5 py-[6px] rounded-md text-[14px] font-semibold text-[#65676B] hover:bg-[#F0F2F5] transition-colors" data-testid={`toggle-comments-${index}`}>
                    <MessageSquare className="w-[18px] h-[18px]" /><span>Comment</span>
                  </button>
                  <div className="flex-1 relative">
                    <button onClick={() => setShareOpen(shareOpen === post.id ? null : post.id)}
                      className="w-full flex items-center justify-center gap-1.5 py-[6px] rounded-md text-[14px] font-semibold text-[#65676B] hover:bg-[#F0F2F5] transition-colors" data-testid={`share-post-${index}`}>
                      <Share2 className="w-[18px] h-[18px]" /><span>Share</span>
                    </button>
                    <AnimatePresence>
                      {shareOpen === post.id && (
                        <SharePostMenu postId={post.id} postText={post.content} onClose={() => setShareOpen(null)} anchor="right" />
                      )}
                    </AnimatePresence>
                  </div>
                </div>

                {/* Comments */}
                <AnimatePresence>
                  {expandedComments[post.id] && (
                    <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
                      <div className="px-4 pb-3 pt-1">
                        <div className="flex items-center gap-2 mb-3">
                          <Avatar user={user} size="w-8 h-8" textSize="text-[11px]" />
                          <div className="flex-1 flex items-center bg-[#F0F2F5] rounded-2xl overflow-hidden">
                            <input type="text" value={newComment[post.id] || ''} onChange={e => setNewComment(prev => ({ ...prev, [post.id]: e.target.value }))}
                              placeholder="Write a comment..." className="flex-1 bg-transparent px-3 py-2 text-[13px] text-[#050505] placeholder:text-[#65676B] focus:outline-none"
                              onKeyDown={e => e.key === 'Enter' && handleComment(post.id)} data-testid={`comment-input-${index}`} />
                            <button onClick={() => handleComment(post.id)} disabled={commentSubmitting[post.id] || !newComment[post.id]?.trim()} className="px-3 py-2 text-[#E63946] disabled:text-[#BCC0C4]" data-testid={`submit-comment-${index}`}>
                              <Send className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                        <div className="space-y-2.5">
                          {comments[post.id]?.map((comment, ci) => {
                            const canDeleteComment = comment.user_id === user?.id || isAdmin;
                            return (
                            <div key={comment.id} className="flex items-start gap-2 group/comment" data-testid={`comment-${index}-${ci}`}>
                              <Link to={`/portal/members/${comment.user_id}`}>
                                <Avatar user={{ id: comment.user_id, name: comment.author?.name, picture: comment.author?.picture }} size="w-8 h-8" textSize="text-[11px]" />
                              </Link>
                              <div className="flex-1 min-w-0">
                                <div className="bg-[#F0F2F5] rounded-2xl px-3 py-2 inline-block max-w-full">
                                  <span className="flex items-center gap-1.5 flex-wrap">
                                    <Link to={`/portal/members/${comment.user_id}`} className="text-[13px] font-semibold text-[#050505] leading-tight hover:underline">{comment.author?.name || 'Member'}</Link>
                                    {comment.author?.leadership_level && <LeadershipBadge badges={comment.author.badges} leadershipLevel={comment.author.leadership_level} leadershipTitle={comment.author.leadership_title} />}
                                    {comment.author?.is_founding_member && !comment.author?.leadership_level && <span className="text-[8px] font-bold bg-amber-100 text-amber-700 px-1 py-0.5 rounded-full">Founder</span>}
                                  </span>
                                  <p className="text-[13px] text-[#050505] mt-0.5 break-words">{comment.content}</p>
                                </div>
                                <div className="flex items-center gap-2 mt-0.5 ml-3">
                                  <p className="text-[11px] text-[#65676B]">{getTimeAgo(comment.created_at)}</p>
                                  {canDeleteComment && (
                                    <button onClick={() => handleDeleteComment(post.id, comment.id)}
                                      className="text-[11px] text-[#65676B] opacity-0 group-hover/comment:opacity-100 hover:text-red-600 transition-opacity"
                                      data-testid={`delete-comment-${index}-${ci}`}>
                                      Delete
                                    </button>
                                  )}
                                </div>
                              </div>
                            </div>
                            );
                          })}
                          {(!comments[post.id] || comments[post.id].length === 0) && <p className="text-[13px] text-[#65676B] text-center py-1">No comments yet</p>}
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
              );
            }) : (
              <div className="bg-white rounded-xl shadow-[0_1px_2px_rgba(0,0,0,0.1)] p-10 text-center">
                <div className="w-16 h-16 bg-[#F0F2F5] rounded-full flex items-center justify-center mx-auto mb-4"><MessageSquare className="w-8 h-8 text-[#BCC0C4]" /></div>
                <h3 className="text-[17px] font-semibold text-[#050505] mb-1">No posts yet</h3>
                <p className="text-[14px] text-[#65676B] max-w-sm mx-auto">Be the first to share something with the DCCF community.</p>
              </div>
            )}
          </div>

          {/* SIDEBAR */}
          <div className="hidden lg:block space-y-4">
            <div className="bg-white rounded-xl shadow-[0_1px_2px_rgba(0,0,0,0.1)] p-4" data-testid="community-quick-actions">
              <h3 className="text-[15px] font-semibold text-[#050505] mb-3">Quick Actions</h3>
              <div className="space-y-1">
                <Link to="/portal/requests" className="flex items-center gap-2.5 px-3 py-2 rounded-lg text-[14px] text-[#050505] hover:bg-[#F0F2F5] transition-colors"><Send className="w-4 h-4 text-[#E63946]" /> Submit a Request</Link>
                <Link to="/portal/blog" className="flex items-center gap-2.5 px-3 py-2 rounded-lg text-[14px] text-[#050505] hover:bg-[#F0F2F5] transition-colors"><MessageSquare className="w-4 h-4 text-[#457B9D]" /> Write a Blog</Link>
                <Link to="/portal/profile" className="flex items-center gap-2.5 px-3 py-2 rounded-lg text-[14px] text-[#050505] hover:bg-[#F0F2F5] transition-colors"><ImageIcon className="w-4 h-4 text-[#F4A261]" /> Update Profile</Link>
                <Link to="/portal/messages" className="flex items-center gap-2.5 px-3 py-2 rounded-lg text-[14px] text-[#050505] hover:bg-[#F0F2F5] transition-colors"><MessageSquare className="w-4 h-4 text-[#2A9D8F]" /> Messages</Link>
              </div>
            </div>
            {events.length > 0 && (
              <div className="bg-white rounded-xl shadow-[0_1px_2px_rgba(0,0,0,0.1)] p-4" data-testid="community-events">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-[15px] font-semibold text-[#050505]">Upcoming Events</h3>
                  <Link to="/portal/events" className="text-[13px] text-[#E63946] font-semibold hover:underline">See all</Link>
                </div>
                <div className="space-y-3">
                  {events.map((event, i) => (
                    <Link key={event.id || i} to="/portal/events" className="flex items-start gap-3 group">
                      <div className="w-12 h-12 bg-[#F0F2F5] rounded-lg flex flex-col items-center justify-center flex-shrink-0">
                        <span className="text-[14px] font-bold text-[#E63946] leading-none">{event.date?.split('-')[2]}</span>
                        <span className="text-[10px] text-[#65676B] uppercase">{new Date(event.date + 'T00:00:00').toLocaleString('en', { month: 'short' })}</span>
                      </div>
                      <div className="min-w-0">
                        <p className="text-[13px] font-semibold text-[#050505] group-hover:text-[#E63946] transition-colors leading-tight truncate">{event.title}</p>
                        <div className="flex items-center gap-2 text-[11px] text-[#65676B] mt-0.5">
                          <span className="flex items-center"><Clock className="w-3 h-3 mr-0.5" />{event.time}</span>
                          <span className="flex items-center"><MapPin className="w-3 h-3 mr-0.5" />{event.location}</span>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}
            <div className="bg-white rounded-xl shadow-[0_1px_2px_rgba(0,0,0,0.1)] p-4">
              <p className="text-[12px] text-[#65676B] leading-relaxed">Dominican Chamber of Commerce of Florida &middot; Connecting communities since 2020.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PortalCommunity;
